/* eslint-disable */
import React from 'react';
import Image from 'frontastic/lib/image';
import Button from 'components/commercetools-ui/atoms/button';
import Link from 'components/commercetools-ui/atoms/link';
import Typography from 'components/commercetools-ui/atoms/typography';
import Markdown from 'components/commercetools-ui/organisms/markdown';
import Breadcrumb from 'components/commercetools-ui/atoms/breadcrumb';
import { ChevronRightIcon } from '@heroicons/react/24/solid';
import { HomeIcon } from '@heroicons/react/24/outline';
import usePath from 'helpers/hooks/usePath';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { CASE_STUDY_DOWNLOAD } from 'helpers/constants/events';

export type ImageWithTextProps = {
  data?: any;
  index?: number;
  totalSections?: number;
};

const ImageWithTextDynamic = ({ data, index, totalSections }: React.PropsWithChildren<ImageWithTextProps>) => {
  const { path } = usePath();

  const generateBreadcrumbs = () => {
    const pathSegments = path.split('/').filter(Boolean);
    const pathSegmentsWithoutLast = pathSegments.slice(0, -1);

    return [
      <span
        key="static-media"
        className="text-12 font-normal capitalize leading-4 text-primary-black"
        style={{ cursor: 'default' }} // Ensure no pointer cursor
      >
        Media
      </span>,
      ...pathSegmentsWithoutLast.map((segment, index) => {
        const href = `/${pathSegmentsWithoutLast.slice(0, index + 1).join('/')}/`;
        const isProductOrBrandSegment =
          segment.includes('products') || segment.includes('brands') || segment.includes('solutions');

        return (
          <span key={segment}>
            {isProductOrBrandSegment ? (
              <span
                className="text-12 font-normal capitalize leading-4 text-primary-black"
                style={{ cursor: 'default' }} // Ensure no pointer cursor for static breadcrumb
              >
                {segment.replace(/-/g, ' ')}
              </span>
            ) : (
              <Link link={href}>
                <span className="text-12 font-normal capitalize leading-4 text-[#666]">
                  {segment.replace(/-/g, ' ')}
                </span>
              </Link>
            )}
          </span>
        );
      }),
    ];
  };

  if (!data?.sections?.find((section: any) => section.section_with_image_text)) {
    console.error('Invalid or missing data for section_with_image_text:', data);
    return null; // Prevent rendering if data is invalid
  }

  const cmsComponentData =
    data?.sections?.find((section: any) => section.section_with_image_text)?.section_with_image_text || '';
  const { desktop_image_alignment = 'Left', mobile_image_alignment = 'Top' } = cmsComponentData;

  const cmsCssClass = cmsComponentData.additional_css || '';

  // Conditional CSS for `/case-studies/` OR /product-releases/ path
  const isCaseStudies = path === '/case-studies/' || path === '/product-releases/';

  const additionalCss = isCaseStudies ? 'lg:px-48 xl:px-60 3xl:px-80' : '';

  // Image padding logic for screens > 1023px
  const imagePaddingClass = isCaseStudies ? '' : 'p-0'; // Remove padding for `/case-studies/ OR /product-releases/`

  const desktopAlignmentClass =
    desktop_image_alignment === 'Left'
      ? 'lg:flex-row'
      : desktop_image_alignment === 'Right'
      ? 'lg:flex-row-reverse'
      : desktop_image_alignment === 'Center'
      ? 'lg:justify-center'
      : '';

  const mobileAlignmentClass =
    mobile_image_alignment === 'Top'
      ? 'flex-col'
      : mobile_image_alignment === 'Bottom'
      ? 'flex-col-reverse'
      : mobile_image_alignment === 'Center'
      ? 'justify-center flex-col'
      : '';
      const handleCTA = (href: string) => {
        if (typeof window === "undefined") return;
        const link = document.createElement("a");
        link.href = href;
        link.target = href.startsWith("https://") ? "_blank" : "_self";
        link.rel = "noopener noreferrer";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      
        (window as any).dataLayer = (window as any).dataLayer || [];
        (window as any).dataLayer.push({
          event: CASE_STUDY_DOWNLOAD,
          file: href,
        });
      };
  return (
    <>
      <div className="hidden px-24 pb-8 pt-16 md:!block md:px-28 lg:px-48 xl:px-60 2xl:pl-60 2xl:pr-80 3xl:pl-80">
        <Breadcrumb Separator={<ChevronRightIcon className="w-16" />} listClassName="!gap-x-16">
          <Link link={'/'}>
            <HomeIcon className="mt-6 w-20" />
          </Link>
          {generateBreadcrumbs()}
        </Breadcrumb>
      </div>
      <div
        className={`${cmsCssClass} flex items-center gap-24 pb-24 lg:gap-0 ${desktopAlignmentClass} ${mobileAlignmentClass}`}
      >
        <div
          className={`relative w-full flex-1 flex-grow ${imagePaddingClass} ${additionalCss}`}
          style={{
            position: 'relative',
            margin: '0',
          }}
        >
          {cmsComponentData?.image?.url && (
            <Image
              loading="lazy"
              src={cmsComponentData.image.url}
              alt={cmsComponentData.image.description || 'Section Image'}
              className="w-full"
              style={{
                height: 'auto',
                maxHeight: 'auto',
                objectFit: 'cover',
              }}
            />
          )}
        </div>
        <div className="flex-1 flex-grow px-[20.5px] sm:px-24 lg:px-48 xl:px-60 3xl:px-80">
          <div className={`${cmsComponentData.additional_css}`}>
            {cmsComponentData?.title && (
              <Markdown markdown={cmsComponentData.title} className="relative pb-10 text-32 font-medium leading-10" />
            )}
            {cmsComponentData?.description && (
              <Markdown
                markdown={cmsComponentData.description}
                className="list-container pt-10 text-left text-16 font-normal leading-6 text-[#333] md:text-18 lg:!text-left lg:leading-8 xl:!text-left 2xl:!text-left 2xl:text-20 [&>p]:mb-16"
              />
            )}
            {cmsComponentData?.ctas &&
              cmsComponentData.ctas.map((cta: any, index: number) => (
                <div className="mt-[50px] flex justify-center lg:justify-start" key={index}>
                  <div onClick={() => handleCTA(cta?.button?.href || '#')}>
                    <Button
                      icon={<ArrowDownTrayIcon className="w-28 text-[#666]" />}
                      iconPosition="middle"
                      className=" "
                      style={{
                        backgroundColor: cta.button_background_color?.hex,
                        border: `1px solid ${cta.button_border_color?.hex}`,
                        color: cta.button_text_color?.hex,
                        cursor: 'pointer !important',
                        padding: '10px 28px',
                        borderRadius: '0',
                      }}
                    >
                      <Typography
                        as="span"
                        style={{
                          fontWeight: 600,
                          fontStyle: 'italic',
                          fontSize: '16px',
                          lineHeight: '20px',
                          cursor: 'pointer !important',
                        }}
                      >
                        {cta.button?.title}
                      </Typography>
                    </Button>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default ImageWithTextDynamic;
